import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
} from '@cinch-labs/design-system'
import { TrackingEvent, useAnalytics } from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import React, {
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types'

import styles from './looping-carousel.module.css'

export interface SwiperContainer extends HTMLElement {
  swiper?: Swiper
}

interface LoopingCarouselProps {
  children: ReactNode
  slideSpeedInSeconds?: number
  shouldLoop?: boolean
  swiperSettings?: SwiperOptions
  className?: {
    container?: string
    carousel?: string
    carouselSlide?: string
    controlsWrapper?: string
    customPagination?: string
  }
}

const useAutoSlide = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  }: {
    shouldLoop: boolean
    slideSpeedInSeconds: number
    isPaused: boolean
  },
) =>
  useEffect(() => {
    if (!shouldLoop || isPaused) return

    const interval = setInterval(() => {
      const swiperContainer = containerRef.current?.querySelector(
        '.swiper',
      ) as SwiperContainer
      swiperContainer?.swiper?.slideNext()
    }, slideSpeedInSeconds * 1000)

    return () => clearInterval(interval)
  }, [shouldLoop, slideSpeedInSeconds, isPaused, containerRef])

export default function LoopingCarousel({
  children,
  slideSpeedInSeconds = 2,
  shouldLoop = true,
  swiperSettings,
  className,
}: LoopingCarouselProps): ReactElement | null {
  const reactId = useId()
  const carouselId = useMemo(
    () => `carousel-${reactId.replace(/:/g, '')}`,
    [reactId],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)

  useAutoSlide(containerRef, {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  })

  if (!children || React.Children.count(children) === 0) {
    return null
  }

  // The design-system Carousel spreads swiperSettings after its own pagination
  // config, so a partial pagination override would drop the `el` wiring derived
  // from the carousel name and silently disable the bullets. Re-apply those
  // name-based defaults before merging the override.
  const { pagination, ...swiperSettingsOverrides } = swiperSettings ?? {}
  const mergedSwiperSettings: SwiperOptions = {
    slidesPerView: 1,
    loop: shouldLoop,
    speed: 600,
    ...swiperSettingsOverrides,
    ...(typeof pagination === 'object'
      ? {
          pagination: {
            el: `.${carouselId}-pagination`,
            clickable: true,
            dynamicBullets: true,
            ...pagination,
          },
        }
      : pagination !== undefined
      ? { pagination }
      : {}),
  }

  return (
    <div
      ref={containerRef}
      className={className?.container}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsPaused(false)
        }
      }}
    >
      <Carousel
        name={carouselId}
        customClassNames={{
          carousel: cx(className?.carousel, styles.carousel),
        }}
        swiperSettings={mergedSwiperSettings}
      >
        {React.Children.map(children, (child, index) => (
          <CarouselSlide
            key={isValidElement(child) && child.key != null ? child.key : index}
            className={cx(className?.carouselSlide, styles.carouselSlide)}
          >
            {child}
          </CarouselSlide>
        ))}
      </Carousel>

      <CarouselControls customClassNames={className?.controlsWrapper}>
        <CarouselPagination
          name={carouselId}
          customClassNames={className?.customPagination}
        />
        <CarouselNavigation
          name={carouselId}
          ariaLabels={{
            previous: 'Go to previous slide',
            next: 'Go to next slide',
          }}
        />
      </CarouselControls>
    </div>
  )
}

/**
 * A minimum visibility ratio (60%) required to count a slide as "viewed".
 * This threshold ensures meaningful impressions—most of the content must be
 * visible, filtering out fast scrolls while allowing for viewport edge cases.
 */
const VISIBILITY_RATIO_TO_COUNT_AS_VIEWED = 0.6

export interface SlideTrackingEvents {
  onClick?: TrackingEvent
  onLoad?: TrackingEvent
}

interface SlideProps {
  children: ReactNode
  trackingEvents?: SlideTrackingEvents
  ariaLabel?: string
  onClick?: () => void
  onImpression?: () => void
}

function useTrackFirstVisibilityImpression(
  onImpression: (() => void) | undefined,
): (node: HTMLDivElement | null) => void {
  const hasTrackedImpressionRef = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  return useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null

      if (!node || !onImpression || hasTrackedImpressionRef.current) {
        return
      }

      observerRef.current = new IntersectionObserver(
        (intersectionEntries) => {
          const intersectionEntry = intersectionEntries[0]
          if (!intersectionEntry) {
            return
          }

          const isVisibleEnough =
            intersectionEntry.isIntersecting &&
            intersectionEntry.intersectionRatio >=
              VISIBILITY_RATIO_TO_COUNT_AS_VIEWED

          if (!isVisibleEnough || hasTrackedImpressionRef.current) {
            return
          }

          hasTrackedImpressionRef.current = true
          onImpression()
          observerRef.current?.disconnect()
          observerRef.current = null
        },
        {
          threshold: VISIBILITY_RATIO_TO_COUNT_AS_VIEWED,
        },
      )

      observerRef.current.observe(node)
    },
    [onImpression],
  )
}

function Slide({
  children,
  trackingEvents,
  ariaLabel,
  onClick,
  onImpression,
}: SlideProps): ReactElement {
  const { trackEvent } = useAnalytics()

  const impressionEvent = trackingEvents?.onLoad
  const slideRootRefCallback = useTrackFirstVisibilityImpression(
    impressionEvent
      ? () => {
          trackEvent(impressionEvent)
          onImpression?.()
        }
      : undefined,
  )

  const handleOnClick = () => {
    if (trackingEvents?.onClick) {
      trackEvent(trackingEvents.onClick)
    }

    onClick?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOnClick()
    }
  }

  const isInteractive = Boolean(onClick || trackingEvents?.onClick)

  return (
    <div
      ref={slideRootRefCallback}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? ariaLabel : undefined}
      onClick={isInteractive ? handleOnClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
    >
      {children}
    </div>
  )
}

LoopingCarousel.Slide = Slide
